import React, { Component } from "react";
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";
import { Row, Col, Container, Form, Modal } from "react-bootstrap";
import * as Cookies from "es-cookie";
import { withApollo, graphql } from "react-apollo";
import { withRouter } from "react-router-dom";
import gql from "graphql-tag";
import compose from "lodash.flowright";
import { getAuthorizedBranchIds } from "../../utils/authHelper";
import { attachModuleGuard, AccessDeniedModal } from "../../../utils/moduleGuard";
import AssignTestModal from "./AssignTestModal";
import CreateAdmissionFormPage from "./CreateAdmissionFormPage";
import FillAdmissionFormPage from "./FillAdmissionFormPage";
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_BODY_FLUSH, ADM_MODAL_HEADER_DOT } from "./admissionModalStyles";
import "./_admissionForms.scss";

// The Enquiry Forms tab is the "Enquiry_management" module. Its grant row has an empty
// sub_module_name, so it's checked by module_key only (no sub-module). module_key ONLY,
// never module_name ("Admission" is shared with the admission_form rows and would leak).
const ENQUIRY_MODULE = ["Enquiry_management"];

const GET_ENQUIRY_FORM_FIELDS_FOR_FILL = gql`
  query ($form_id: Int!, $institute_id: Int) {
    getEnquiryFormFields(form_id: $form_id, institute_id: $institute_id) {
      id
      field_name
      field_key
      field_type_id
      field_type
      options
      is_required
      value_length
      order_no
    }
  }
`;

const GET_ENQUIRY_FORMS = gql`
  query ($institute_id: Int!, $branch_id: String!) {
    getEnquiryForms(institute_id: $institute_id, branch_id: $branch_id) {
      form_name
      id
      lead_count
      source_details {
        source_name
        source_count
      }
      fields {
        id
        field_name
        field_type_id
      }
    }
  }
`;


class AdmissionFormsHome extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedBranches: [],
      branchError: "",
      loader: false,
      admissionFormData: [],
      noDataError: "",

      // Pagination for the forms list
      formsPage: 1,
      formsPerPage: 5,

      // Create/Edit form modal
      showModal: false,
      editingForm: null,
      currentBranchId: "",

      // Copy link
      copySuccess: {},

      // Assign Test modal
      showAssignModal: false,
      selectedStudent: null,

      customSources: {},

      // Create Enquiry Form modal
      showCreateModal: false,
      createModalBranchId: "",

      // Edit Enquiry Form modal
      showEditModal: false,
      editingFormItem: null,

      // Add Enquiry (fill form) modal
      showFillModal: false,
      fillFormId: 0,
      fillBranchId: "",
      fillFormName: "",
      fillFormFields: [],
      fillFormLoading: false,

      // Flipped to true by the guard when a role without is_edit clicks a write action.
      accessDeniedShow: false,
    };

    // Buttons stay VISIBLE; the guard intercepts the click and opens the Access Denied
    // modal when the role lacks is_edit on the Enquiry_management module.
    this.guard = attachModuleGuard(this, ENQUIRY_MODULE);
  }

  componentDidMount() {
    this.initBranches();
    // Branch comes from the header (NavbarOne) — re-scope when it changes.
    this.offBranchScope = onBranchScopeChange(() => this.initBranches());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  componentDidUpdate(prevProps) {
    const branches = this.props.globals && this.props.globals.globalBranches;
    const prevBranches = prevProps.globals && prevProps.globals.globalBranches;
    if (branches && branches !== prevBranches && this.state.selectedBranches.length === 0) {
      this.initBranches();
    }
  }

  // selectedBranches simply mirrors the header's branch scope (NavbarOne) — this page
  // has no branch picker of its own.
  initBranches = () => {
    const branches = scopeBranches(this.props.globals?.globalBranches || []);
    if (branches.length > 0) {
      const selected = branches.map((b) => ({ label: b.branch_name, value: b.id }));
      this.setState({ selectedBranches: selected }, () => {
        this.fetchForms(selected);
      });
    }
  }

  handleBranchChange = (selected) => {
    this.setState({
      selectedBranches: selected,
      branchError: "",
      admissionFormData: [],
      noDataError: "",
    }, () => {
      if (selected && selected.length > 0) {
        this.fetchForms(selected);
      } else {
        this.setState({ noDataError: "Please select at least one branch to view enquiry forms." });
      }
    });
  };

  // Single-select branch dropdown (same "All Branches" style as the Admission List).
  // "" = All Branches → selects every authorized branch; otherwise just the chosen one.
  handleBranchSelectChange = (e) => {
    const val = e.target.value;
    const authorizedIds = getAuthorizedBranchIds();
    const branchOptions = (
      this.props.globals && this.props.globals.globalBranches ? this.props.globals.globalBranches : []
    ).filter((b) => authorizedIds.includes(String(b.id)))
      .map((b) => ({ label: b.branch_name || `Branch ${b.id}`, value: b.id }));
    const selected = val === ""
      ? branchOptions
      : branchOptions.filter((b) => String(b.value) === String(val));
    this.handleBranchChange(selected);
  };

  fetchForms = async (branches, { silent = false } = {}) => {
    if (!branches || branches.length === 0) {
      this.setState({ branchError: "Please select at least one branch" });
      return;
    }
    if (silent) {
      this.setState({ noDataError: "", branchError: "" });
    } else {
      this.setState({
        loader: true,
        admissionFormData: [],
        noDataError: "",
        branchError: "",
      });
    }

    try {
      const promises = branches.map((branch) =>
        this.props.client
          .query({
            query: GET_ENQUIRY_FORMS,
            variables: {
              institute_id: parseInt(Cookies.get("institutionid")),
              branch_id: String(branch.value),
            },
            fetchPolicy: "network-only",
          })
          .then(async (result) => {
            const rawForms = result.data.getEnquiryForms || [];

            // For each form, compute form_url and map pre-fetched fields
            const enrichedForms = rawForms.map((f) => ({
              ...f,
              form_url: `${window.location.origin}/enquiry-form/${f.id}?inst=${parseInt(Cookies.get("institutionid"))}&branch=${branch.value}`,
              fieldNames: (f.fields || []).map((field) => field.field_name),
              existing_form_field_id: f.id,
              branch_id: branch.value,
            }));

            return {
              branch_name: branch.label,
              branch_id: branch.value,
              forms: enrichedForms,
            };
          }),
      );

      const results = await Promise.all(promises);
      const withData = results.filter((r) => r.forms.length > 0);

      if (withData.length > 0) {
        this.setState({ admissionFormData: withData, loader: false, formsPage: 1 });
      } else {
        this.setState({
          loader: false,
          noDataError: "No enquiry forms found for the selected branch(es).",
        });
      }
    } catch (err) {
      this.setState({
        loader: false,
        noDataError: "Failed to load data. Please try again.",
      });
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.fetchForms(this.state.selectedBranches);
  };

  handleCopy = (textToCopy, stateKey) => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      this.setState({ copySuccess: { [stateKey]: true } });
      setTimeout(() => this.setState({ copySuccess: { [stateKey]: false } }), 2000);
    });
  };

  resolveFieldNames = (item) => {
    // These 12 fields are standard mandatory fields for all forms
    const standardMandatory = [
      "Name", "Primary Contact Number", "Date of Birth", "Gender", "Enquiry Class",
    ];

    // item.fieldNames contains ALL field names fetched per-form.
    // We need to split mandatory vs optional, and deduplicate.
    const allNames = item.fieldNames || [];
    const isMandatoryMatch = (name) => {
      const l = (name || "").toLowerCase().trim();
      return (
        l === "name" ||
        l === "student name" ||
        l === "primary contact number" ||
        l === "mobile" ||
        l === "mobile number" ||
        l === "student mobile"
      );
    };

    const seen = new Set();
    const mandatory = [];
    const optional = [];

    // Always include the standard 6 first
    standardMandatory.forEach(m => {
      mandatory.push(m);
      seen.add(m.toLowerCase());
    });

    allNames.forEach((n) => {
      const l = (n || "").toLowerCase().trim();
      if (seen.has(l)) return;
      seen.add(l);

      if (isMandatoryMatch(n)) {
        mandatory.push(n);
      } else {
        optional.push(n);
      }
    });

    return { mandatory, optional };
  };

  openCreate = () => {
    // "Create Enquiry Form" is a write → is_edit. Pops Access Denied without the grant.
    if (!this.guard.requireEdit()) return;
    const { selectedBranches } = this.state;
    let branchId = "";
    if (selectedBranches && selectedBranches.length > 0) {
      branchId = selectedBranches[0].value;
    }
    this.setState({ showCreateModal: true, createModalBranchId: branchId });
  };

  handleCreateModalClose = ({ saved } = {}) => {
    this.setState({ showCreateModal: false, createModalBranchId: "" }, () => {
      if (saved && this.state.selectedBranches.length > 0) {
        this.fetchForms(this.state.selectedBranches, { silent: true });
      }
    });
  };

  openEdit = (item) => {
    // "Edit Admission Configuration" is a write → is_edit.
    if (!this.guard.requireEdit()) return;
    this.setState({ showEditModal: true, editingFormItem: item });
  };

  handleEditModalClose = ({ saved } = {}) => {
    this.setState({ showEditModal: false, editingFormItem: null }, () => {
      if (saved && this.state.selectedBranches.length > 0) {
        this.fetchForms(this.state.selectedBranches, { silent: true });
      }
    });
  };

  closeModal = () => {
    this.setState({ showModal: false, editingForm: null });
  };

  // ── Fill Form (Add Enquiry modal) ──────────
  openCreateStudent = async (item, branchId) => {
    // "Add Enquiry" creates a lead → write → is_edit.
    if (!this.guard.requireEdit()) return;
    this.setState({
      showFillModal: true,
      fillFormId: parseInt(item.id),
      fillBranchId: branchId,
      fillFormName: item.form_name,
      fillFormFields: [],
      fillFormLoading: true,
    });
    try {
      const result = await this.props.client.query({
        query: GET_ENQUIRY_FORM_FIELDS_FOR_FILL,
        variables: {
          form_id: parseInt(item.id),
          institute_id: parseInt(Cookies.get("institutionid")),
        },
        fetchPolicy: "network-only",
      });
      this.setState({
        fillFormFields: result.data.getEnquiryFormFields || [],
        fillFormLoading: false,
      });
    } catch {
      this.setState({ fillFormLoading: false });
    }
  };

  handleFillModalClose = () => {
    this.setState({
      showFillModal: false,
      fillFormId: 0,
      fillBranchId: "",
      fillFormName: "",
      fillFormFields: [],
      fillFormLoading: false,
    }, () => {
      if (this.state.selectedBranches.length > 0) {
        this.fetchForms(this.state.selectedBranches, { silent: true });
      }
    });
  };

  // ── Assign Test (from card directly) ─────────
  openAssignModal = (student) => {
    this.setState({ showAssignModal: true, selectedStudent: student });
  };

  closeAssignModal = () => {
    this.setState({ showAssignModal: false, selectedStudent: null });
  };

  render() {
    const {
      admissionFormData,
      loader,
      noDataError,
      editingForm,
      copySuccess,
      selectedBranches,
      showAssignModal,
      selectedStudent,
    } = this.state;
    const { globals } = this.props;

    const authorizedIds = getAuthorizedBranchIds();

    const branchOptions = (
      globals && globals.globalBranches ? globals.globalBranches : []
    ).filter(b => authorizedIds.includes(String(b.id)))
      .map((b) => ({
        label: b.branch_name || `Branch ${b.id}`,
        value: b.id
      }));


    const totalForms = admissionFormData.reduce(
      (sum, g) => sum + g.forms.length,
      0,
    );

    return (
      <Container fluid className="px-0 pb-5">
        <style>{`
          /* Aggressive CSS Reset for ANY MultiSelect Library */
          div[class*="rmsc"], div[class*="multi-select"], div[class*="dropdown-content"] {
            --rmsc-bg: #fff !important;
            --rmsc-hover: #f1f5f9 !important;
            --rmsc-border: #e2e8f0 !important;
          }
          
          /* Target Select Container */
          div[class*="dropdown-container"] {
            border-radius: 6px !important;
            border-color: #e2e8f0 !important;
            min-height: 40px !important;
            background: #fff !important;
          }

          /* Target Dropdown Items */
          div[class*="dropdown-content"] {
             background: #fff !important;
             border-radius: 6px !important;
             box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1) !important;
             border: 1px solid #e2e8f0 !important;
             z-index: 9999 !important;
          }

          div[class*="dropdown-content"] *, 
          div[class*="dropdown-content"] label,
          div[class*="dropdown-content"] li,
          div[class*="item-renderer"],
          div[class*="select-item"] {
            background-color: transparent !important;
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            color: #334155 !important;
          }

          /* Hover State */
          div[class*="dropdown-content"] label:hover,
          div[class*="dropdown-content"] li:hover,
          div[class*="select-item"]:hover {
            background-color: #f1f5f9 !important;
            background: #f1f5f9 !important;
          }

          /* Search bar */
          div[class*="dropdown-content"] input[type="text"] {
             background-color: #f8fafc !important;
             border: 1px solid #e2e8f0 !important;
             border-radius: 4px !important;
             margin: 4px !important;
             padding: 8px !important;
          }

          .af-dash-multiselect .rmsc { --rmsc-main: #3b82f6; --rmsc-hover: #f1f5f9; --rmsc-selected: #e2e8f0; --rmsc-border: #cbd5e1; --rmsc-gray: #64748b; --rmsc-bg: #fff; }
          .af-dash-multiselect .dropdown-container { border: 1px solid #cbd5e1; border-radius: 6px; }

          .badge-soft-success { background-color: #dcfce7; color: #166534; }
          .badge-soft-warning { background-color: #fef3c7; color: #92400e; }

          @media print {
            .header-area, #sidebar, .af-filter-bar, .btn { display: none !important; }
            .main-wrapper { margin-left: 0 !important; }
            .content-wrapper { padding: 0 !important; }
          }
        `}</style>

        <div>
          <div style={{ padding: "28px 0" }}>
            <>
                {/* ── Dashboard Header & Filter ─────────────────────────────── */}
                <div
                  className="af-admin-header mb-4 pb-3 border-bottom d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center"
                  style={{ gap: "16px" }}
                >
                  <div>
                    <h4 className="font-weight-bold mb-1" style={{ color: "#0f172a" }}>
                      <i className="fas fa-folder-open mr-2 text-primary opacity-75" />
                      Manage Enquiry Forms
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                      View and manage enquiry forms configurations and entries across
                      branches.
                    </p>
                  </div>

          <div
            className="d-flex flex-column flex-md-row align-items-start align-items-md-center w-100"
            style={{ gap: "16px", flex: 1, justifyContent: "flex-end" }}
          >
            <div
              className="d-flex align-items-center pb-0 border-right-0 border-md-right pr-0 pr-md-3 mr-0 mr-md-2"
              style={{ gap: "8px" }}
            >
              <button
                className="af-save-btn d-flex align-items-center"
                onClick={this.openCreate}
                style={{ height: "38px", gap: "8px", whiteSpace: "nowrap", flexShrink: 0 }}
              >
                <i className="fas fa-plus"></i> Create Enquiry Form
              </button>
            </div>
            <div
              className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center m-0"
              style={{ gap: "12px" }}
            >
              {/* No Branch filter — the header (NavbarOne) scopes this page. */}
            </div>
          </div>
        </div>

        {/* ---- Loading ---- */}
        {loader && (
          <div className="af-cards-grid mt-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="af-shadcn-card d-flex flex-column h-100 p-4" style={{ height: "300px" }}>
                <div className="d-flex justify-content-between mb-4">
                  <div className="w-75">
                    <span className="af-skeleton-box w-50 mb-2" style={{ height: "12px", display: "block" }}></span>
                    <span className="af-skeleton-box w-100" style={{ height: "20px", display: "block" }}></span>
                  </div>
                  <span className="af-skeleton-box" style={{ width: "40px", height: "24px", borderRadius: "99px" }}></span>
                </div>
                <span className="af-skeleton-box w-50 mb-2" style={{ height: "14px", display: "block" }}></span>
                <span className="af-skeleton-box w-25 mb-4" style={{ height: "14px", display: "block" }}></span>

                <span className="af-skeleton-box w-100 mb-2" style={{ height: "12px", display: "block" }}></span>
                <span className="af-skeleton-box w-100 mb-2" style={{ height: "12px", display: "block" }}></span>
                <span className="af-skeleton-box w-75 mb-4" style={{ height: "12px", display: "block" }}></span>

                <div className="mt-auto pt-3 border-top" style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                  <div className="d-flex w-100" style={{ gap: "8px" }}>
                    <span className="af-skeleton-box w-100" style={{ height: "32px" }}></span>
                    <span className="af-skeleton-box w-100" style={{ height: "32px" }}></span>
                    <span className="af-skeleton-box w-100" style={{ height: "32px" }}></span>
                  </div>
                  <span className="af-skeleton-box w-100" style={{ height: "38px" }}></span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---- Empty / Error ---- */}
        {!loader && noDataError && (
          <div className="af-empty-state">
            <i className="fas fa-folder-open af-empty-icon" />
            <h6>No Forms Found</h6>
            <p>{noDataError}</p>
          </div>
        )}

        {/* ---- Form Cards ---- */}
        {!loader && admissionFormData.length > 0 && (() => {
          // Flatten all forms (with branch info), paginate, then regroup the page.
          const { formsPage, formsPerPage } = this.state;
          const flatForms = admissionFormData.flatMap((g) =>
            g.forms.map((f) => ({ ...f, _branch_id: g.branch_id, _branch_name: g.branch_name })),
          );
          const totalPages = Math.max(1, Math.ceil(flatForms.length / formsPerPage));
          const page = Math.min(formsPage, totalPages);
          const start = (page - 1) * formsPerPage;
          const pageForms = flatForms.slice(start, start + formsPerPage);

          // Regroup the current page's forms by branch (preserving order).
          const pageGroups = [];
          const groupIndex = {};
          pageForms.forEach((f) => {
            if (groupIndex[f._branch_id] === undefined) {
              groupIndex[f._branch_id] = pageGroups.length;
              pageGroups.push({ branch_id: f._branch_id, branch_name: f._branch_name, forms: [] });
            }
            pageGroups[groupIndex[f._branch_id]].forms.push(f);
          });

          return (
          <>
            <p className="af-results-summary">
              {totalForms} form{totalForms !== 1 ? "s" : ""}
              &nbsp;&middot;&nbsp;
              {admissionFormData.length} branch
              {admissionFormData.length !== 1 ? "es" : ""}
              &nbsp;&middot;&nbsp;
              Page {page} of {totalPages}
            </p>

            {pageGroups.map((group) => (
              <div key={group.branch_id} className="af-branch-group">
                <div className="af-branch-banner">
                  <i className="fas fa-building" />
                  {group.branch_name}
                  <span className="af-branch-count">
                    {group.forms.length} form
                    {group.forms.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className="af-smart-list">
                  {group.forms.map((item, index) => {
                    const fieldNames = this.resolveFieldNames(item);
                    const isCopied = this.state.copySuccess?.[item.form_url];
                    const totalFields = fieldNames.mandatory.length + fieldNames.optional.length;
                    return (
                      <div className="af-list-row-container" key={item.existing_form_field_id}>
                        <div className="af-list-row">
                          <div className="af-row-main">
                            <div className="af-row-icon">
                              <i className="fas fa-file-alt" />
                            </div>
                            <div className="af-row-info">
                              <h6>{item.form_name}</h6>
                              <div className="af-row-sub">
                                <span><i className="fas fa-list-ul mr-1" /> {totalFields} Fields</span>
                                <span>&bull;</span>
                                <span><i className="fas fa-building mr-1" /> {group.branch_name}</span>
                              </div>
                            </div>
                          </div>

                          <div className="af-row-actions">
                            {/* <input
                              type="text"
                              placeholder="Source (e.g. facebook)"
                              className="form-control form-control-sm mr-2 d-inline-block"
                              style={{ width: "135px", fontSize: "12px", height: "32px", margin: "auto 0" }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                this.setState({
                                  customSources: { ...this.state.customSources, [item.form_url]: e.target.value }
                                });
                              }}
                              value={this.state.customSources?.[item.form_url] || ""}
                            /> */}
                            <button
                              className={`af-action-pill-btn copy-btn ${isCopied ? "success" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // const sourceVal = this.state.customSources?.[item.form_url] || "";
                                // const finalUrl = sourceVal ? `${item.form_url}&source=${encodeURIComponent(sourceVal)}` : item.form_url;
                                const finalUrl = item.form_url;
                                this.handleCopy(finalUrl, item.form_url);
                              }}
                              title="Copy Form Link"
                            >
                              <i className={`fas ${isCopied ? "fa-check" : "fa-link"}`} />
                              <span>{isCopied ? "Copied" : "Copy Form Link"}</span>
                            </button>

                            <button
                              className="af-action-pill-btn edit-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.setState({ currentBranchId: group.branch_id });
                                this.openEdit(item);
                              }}
                              title="Edit Form Configuration"
                            >
                              <i className="fas fa-sliders-h" />
                              <span>Edit Admission Configuration</span>
                            </button>

                            <div className="af-action-divider" />

                            <button
                              className="af-primary-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.openCreateStudent(item, group.branch_id);
                              }}
                            >
                              <i className="fas fa-plus-circle" />
                              Add Enquiry
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* ---- Pagination ---- */}
            {flatForms.length > 0 && totalPages > 1 && (
              <div className="af-pagination">
                <button
                  className="af-page-nav"
                  disabled={page <= 1}
                  onClick={() => this.setState({ formsPage: page - 1 })}
                >
                  <i className="fas fa-chevron-left" /> Prev
                </button>
                <div className="af-page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      className={`af-page-num ${p === page ? "active" : ""}`}
                      onClick={() => this.setState({ formsPage: p })}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  className="af-page-nav"
                  disabled={page >= totalPages}
                  onClick={() => this.setState({ formsPage: page + 1 })}
                >
                  Next <i className="fas fa-chevron-right" />
                </button>
              </div>
            )}
          </>
          );
        })()}
              </>
          </div>
        </div>



        {/* ---- Create Enquiry Form Modal ---- */}
        <Modal
          show={this.state.showCreateModal}
          onHide={() => this.handleCreateModalClose({ saved: false })}
          size="xl"
          centered
          backdrop="static"
          dialogClassName="af-create-form-modal"
        >
          <Modal.Header closeButton style={ADM_MODAL_HEADER}>
            <Modal.Title style={ADM_MODAL_TITLE}>
              <span style={ADM_MODAL_HEADER_DOT} />
              <i className="fal fa-file-signature" />
              <span>
                Create Enquiry Form
                <span style={ADM_MODAL_TITLE_SUB}>Design fields, choose a branch, and publish</span>
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={ADM_MODAL_BODY_FLUSH}>
            {this.state.showCreateModal && (
              <CreateAdmissionFormPage
                globals={this.props.globals}
                getEnquiryFormFields={this.props.getEnquiryFormFields}
                stateData={{ branch_id: this.state.createModalBranchId }}
                onClose={this.handleCreateModalClose}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* ---- Add Enquiry (Fill Form) Modal ---- */}
        <Modal
          show={this.state.showFillModal}
          onHide={this.handleFillModalClose}
          size="xl"
          centered
          backdrop="static"
          dialogClassName="af-create-form-modal"
        >
          <Modal.Header closeButton style={ADM_MODAL_HEADER}>
            <Modal.Title style={ADM_MODAL_TITLE}>
              <span style={ADM_MODAL_HEADER_DOT} />
              <i className="fal fa-plus-circle" />
              <span>
                Add Enquiry
                <span style={ADM_MODAL_TITLE_SUB}>
                  {this.state.fillFormName || "Capture a new prospective student"}
                </span>
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={ADM_MODAL_BODY_FLUSH}>
            {this.state.showFillModal && (
              <FillAdmissionFormPage
                globals={this.props.globals}
                getEnquiryFormFields={{
                  loading: this.state.fillFormLoading,
                  getEnquiryFormFields: this.state.fillFormFields,
                }}
                stateData={{ form_name: this.state.fillFormName, branch_id: this.state.fillBranchId }}
                formId={this.state.fillFormId}
                onClose={this.handleFillModalClose}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* ---- Edit Enquiry Form Modal ---- */}
        <Modal
          show={this.state.showEditModal}
          onHide={() => this.handleEditModalClose({ saved: false })}
          size="xl"
          centered
          backdrop="static"
          dialogClassName="af-create-form-modal"
        >
          <Modal.Header closeButton style={ADM_MODAL_HEADER}>
            <Modal.Title style={ADM_MODAL_TITLE}>
              <span style={ADM_MODAL_HEADER_DOT} />
              <i className="fal fa-sliders-h" />
              <span>
                Edit Form Configuration
                <span style={ADM_MODAL_TITLE_SUB}>
                  {this.state.editingFormItem?.form_name || "Update fields and form settings"}
                </span>
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={ADM_MODAL_BODY_FLUSH}>
            {this.state.showEditModal && this.state.editingFormItem && (
              <CreateAdmissionFormPage
                globals={this.props.globals}
                getEnquiryFormFields={this.props.getEnquiryFormFields}
                stateData={{
                  existingAdmissionFormFields: [this.state.editingFormItem],
                  branch_id: this.state.editingFormItem.branch_id,
                }}
                onClose={this.handleEditModalClose}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* ---- Assign Test Modal (direct from card) ---- */}
        <AssignTestModal
          show={showAssignModal}
          onHide={this.closeAssignModal}
          student={selectedStudent}
        />

        {/* Shown when a role without is_edit clicks Create / Edit Enquiry Form or Add Enquiry. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />

      </Container>
    );
  }
}

export default compose(
  withRouter,
  withApollo,
)(AdmissionFormsHome);
